import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useEffect, useRef, useState } from 'react';
import { Footer } from '../components/layout/Footer';
import { blogService } from '../services/blogService';
import { BlogPost } from '../types/blog';
import { Calendar, Clock, ArrowRight } from 'lucide-react';

export default function Landing() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const hasRedirected = useRef(false);
  const [recentPosts, setRecentPosts] = useState<BlogPost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(true);

  useEffect(() => {
    if (user && !loading && !hasRedirected.current) {
      hasRedirected.current = true;
      navigate('/home', { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const fetchRecentPosts = async () => {
      try {
        const posts = await blogService.getPublishedPosts({ limit: 3 });
        setRecentPosts(posts);
      } catch (error) {
        console.error('Failed to fetch recent posts:', error);
      } finally {
        setLoadingPosts(false);
      }
    };

    fetchRecentPosts();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-throttle-navy-900 via-throttle-navy-500 to-throttle-navy-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-throttle-navy-900 via-throttle-navy-500 to-throttle-navy-900 flex flex-col">
      <header className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img
              src="/images/copy_of_grunge_urban_spray_music_studio_logo_(2).png"
              alt="Social Pack"
              className="h-48 sm:h-60 w-auto"
            />
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <Link
              to="/blog"
              className="text-white hover:text-throttle-cyan-400 font-medium transition-colors text-sm sm:text-base"
            >
              Blog
            </Link>
            <button
              onClick={() => navigate('/login')}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-all duration-200 border border-white/30 hover:border-white/50 backdrop-blur"
            >
              Login
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-12 sm:py-20 flex-grow">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-header font-bold text-white mb-4 sm:mb-6 leading-tight">
            Upload a selfie. Get 10 branded Lifestyle-Images in under 5 minutes.
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-8 sm:mb-10 px-4 max-w-4xl mx-auto accent-lowercase">
            choose your outfit style • pick your town/scene • upload logo or brand colors • download or post directly to Canva/Instagram/Facebook — captions & alt-text included.
          </p>

          <div className="flex flex-col items-center gap-2 mb-3 px-4">
            <button
              onClick={() => navigate('/pricing')}
              className="w-full sm:w-auto px-8 sm:px-12 py-4 sm:py-5 bg-throttle-cyan-500 text-throttle-navy-900 text-lg sm:text-xl font-header font-bold rounded-lg hover:bg-throttle-cyan-400 transition-all duration-200 min-h-[56px] shadow-2xl shadow-throttle-cyan-500/60 hover:shadow-throttle-cyan-500/80 hover:scale-105"
            >
              Start Your Pack Now
            </button>
          </div>

          <p className="text-base sm:text-lg text-throttle-cyan-400 font-accent mb-16 sm:mb-20 lowercase">
            join 150+ professionals who've upgraded their content without a photoshoot.
          </p>

          <div className="px-4">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-header font-bold text-white mb-3 sm:mb-4 text-center">See the Transformation</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-8 sm:mb-12 text-center">Real people just like you. Real results.</p>

            <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-4 sm:p-6 md:p-8 border border-slate-700">
              <div className="flex flex-col md:grid md:grid-cols-2 gap-6 sm:gap-8 items-center relative">
                <div>
                  <div className="text-center mb-4">
                    <span className="inline-block px-4 py-2 bg-slate-700 text-white rounded-full text-sm font-semibold mb-4">
                      Before
                    </span>
                  </div>
                  <div className="rounded-xl overflow-hidden border-2 border-slate-600">
                    <img
                      src="/images/examples/image5.jpeg"
                      alt="Before - Simple green screen photo"
                      className="w-full h-auto"
                    />
                  </div>
                  <p className="text-gray-400 text-center mt-4 text-sm sm:text-base">Simple green screen photo</p>
                </div>

                <div className="hidden md:flex absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                  <div className="w-12 h-12 bg-throttle-cyan-500 rounded-full flex items-center justify-center shadow-lg shadow-throttle-cyan-500/50">
                    <svg className="w-6 h-6 text-throttle-navy-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </div>
                </div>

                <div className="flex md:hidden justify-center py-2">
                  <div className="w-12 h-12 bg-throttle-cyan-500 rounded-full flex items-center justify-center shadow-lg shadow-throttle-cyan-500/50">
                    <svg className="w-6 h-6 text-throttle-navy-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>
                </div>

                <div>
                  <div className="text-center mb-4">
                    <span className="inline-block px-4 py-2 bg-throttle-cyan-500 text-throttle-navy-900 rounded-full text-sm font-semibold mb-4 shadow-lg shadow-throttle-cyan-500/30">
                      AFTER
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="rounded-lg overflow-hidden border border-slate-600">
                      <img
                        src="/images/examples/keys_to_your_new_beginning.png"
                        alt="Generated example 1"
                        className="w-full h-auto"
                      />
                    </div>
                    <div className="rounded-lg overflow-hidden border border-slate-600">
                      <img
                        src="/images/examples/lake_havasu_closing_day.png"
                        alt="Generated example 2"
                        className="w-full h-auto"
                      />
                    </div>
                    <div className="rounded-lg overflow-hidden border border-slate-600">
                      <img
                        src="/images/examples/riding_the_wave_of_the_market.png"
                        alt="Generated example 3"
                        className="w-full h-auto"
                      />
                    </div>
                    <div className="rounded-lg overflow-hidden border border-slate-600">
                      <img
                        src="/images/examples/summer_market_expert (1).png"
                        alt="Generated example 4"
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                  <p className="text-gray-400 text-center mt-4 text-sm sm:text-base">Professional branded posts ready to share</p>
                </div>
              </div>
            </div>

            <div className="my-12 sm:my-16">
              <div className="max-w-2xl mx-auto space-y-6">
                <div className="flex items-start gap-4 bg-slate-800/30 rounded-xl p-6 backdrop-blur">
                  <div className="flex-shrink-0 w-8 h-8 bg-throttle-cyan-500 rounded-full flex items-center justify-center mt-1">
                    <svg className="w-5 h-5 text-throttle-navy-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-lg sm:text-xl text-white font-body font-semibold">Delivered in under 5 minutes.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-slate-800/30 rounded-xl p-6 backdrop-blur">
                  <div className="flex-shrink-0 w-8 h-8 bg-throttle-cyan-500 rounded-full flex items-center justify-center mt-1">
                    <svg className="w-5 h-5 text-throttle-navy-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-lg sm:text-xl text-white font-body font-semibold">Fully branded: your logo, colors, local town-scene.</p>
                  </div>
                </div>
              </div>
              <div className="flex justify-center mt-8">
                <button
                  onClick={() => navigate('/pricing')}
                  className="px-8 sm:px-12 py-4 sm:py-5 bg-throttle-cyan-500 text-throttle-navy-900 text-lg sm:text-xl font-bold rounded-lg hover:bg-throttle-cyan-400 transition-all duration-200 shadow-2xl shadow-throttle-cyan-500/60 hover:shadow-throttle-cyan-500/80 hover:scale-105"
                >
                  Start Your Pack Now
                </button>
              </div>
            </div>

            <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-4 sm:p-6 md:p-8 border border-slate-700">
              <div className="flex flex-col md:grid md:grid-cols-2 gap-6 sm:gap-8 items-center relative">
                <div>
                  <div className="text-center mb-4">
                    <span className="inline-block px-4 py-2 bg-slate-700 text-white rounded-full text-sm font-semibold mb-4">
                      Before
                    </span>
                  </div>
                  <div className="rounded-xl overflow-hidden border-2 border-slate-600">
                    <img
                      src="/images/examples/image1 (1).jpeg"
                      alt="Before - Professional portrait photo"
                      className="w-full h-auto"
                    />
                  </div>
                  <p className="text-gray-400 text-center mt-4 text-sm sm:text-base">Professional portrait photo</p>
                </div>

                <div className="hidden md:flex absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                  <div className="w-12 h-12 bg-throttle-cyan-500 rounded-full flex items-center justify-center shadow-lg shadow-throttle-cyan-500/50">
                    <svg className="w-6 h-6 text-throttle-navy-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </div>
                </div>

                <div className="flex md:hidden justify-center py-2">
                  <div className="w-12 h-12 bg-throttle-cyan-500 rounded-full flex items-center justify-center shadow-lg shadow-throttle-cyan-500/50">
                    <svg className="w-6 h-6 text-throttle-navy-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>
                </div>

                <div>
                  <div className="text-center mb-4">
                    <span className="inline-block px-4 py-2 bg-throttle-cyan-500 text-throttle-navy-900 rounded-full text-sm font-semibold mb-4 shadow-lg shadow-throttle-cyan-500/30">
                      AFTER
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="rounded-lg overflow-hidden border border-slate-600">
                      <img
                        src="/images/examples/golden_hour_greetings copy.png"
                        alt="Generated example - Golden hour greeting"
                        className="w-full h-auto"
                      />
                    </div>
                    <div className="rounded-lg overflow-hidden border border-slate-600">
                      <img
                        src="/images/examples/key_to_your_new_home copy.png"
                        alt="Generated example - Key to your new home"
                        className="w-full h-auto"
                      />
                    </div>
                    <div className="rounded-lg overflow-hidden border border-slate-600">
                      <img
                        src="/images/examples/the_sign_of_success copy.png"
                        alt="Generated example - Sign of success"
                        className="w-full h-auto"
                      />
                    </div>
                    <div className="rounded-lg overflow-hidden border border-slate-600">
                      <img
                        src="/images/examples/the_approachable_advisor copy.png"
                        alt="Generated example - The approachable advisor"
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                  <p className="text-gray-400 text-center mt-4 text-sm sm:text-base">Ready-to-post branded content</p>
                </div>
              </div>
            </div>

            <div className="my-16 sm:my-20">
              <div className="max-w-2xl mx-auto space-y-6 mb-8">
                <div className="flex items-start gap-4 bg-slate-800/30 rounded-xl p-6 backdrop-blur">
                  <div className="flex-shrink-0 w-8 h-8 bg-throttle-cyan-500 rounded-full flex items-center justify-center mt-1">
                    <svg className="w-5 h-5 text-throttle-navy-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-lg sm:text-xl text-white font-body font-semibold">100+ vetted prompts for professional scene creations.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-slate-800/30 rounded-xl p-6 backdrop-blur">
                  <div className="flex-shrink-0 w-8 h-8 bg-throttle-cyan-500 rounded-full flex items-center justify-center mt-1">
                    <svg className="w-5 h-5 text-throttle-navy-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-lg sm:text-xl text-white font-body font-semibold">Download or post direct + captions & alt-text included.</p>
                  </div>
                </div>
              </div>
              <div className="flex justify-center mb-12 sm:mb-16">
                <button
                  onClick={() => navigate('/pricing')}
                  className="px-8 sm:px-12 py-4 sm:py-5 bg-throttle-cyan-500 text-throttle-navy-900 text-lg sm:text-xl font-bold rounded-lg hover:bg-throttle-cyan-400 transition-all duration-200 shadow-2xl shadow-throttle-cyan-500/60 hover:shadow-throttle-cyan-500/80 hover:scale-105"
                >
                  Start Your Pack Now
                </button>
              </div>
            </div>

            <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-4 sm:p-6 md:p-8 border border-slate-700">
              <div className="flex flex-col md:grid md:grid-cols-2 gap-6 sm:gap-8 items-center relative">
                <div>
                  <div className="text-center mb-4">
                    <span className="inline-block px-4 py-2 bg-slate-700 text-white rounded-full text-sm font-semibold mb-4">
                      Before
                    </span>
                  </div>
                  <div className="rounded-xl overflow-hidden border-2 border-slate-600">
                    <img
                      src="/images/examples/image0 (3).jpeg"
                      alt="Before - Casual selfie photo"
                      className="w-full h-auto"
                    />
                  </div>
                  <p className="text-gray-400 text-center mt-4 text-sm sm:text-base">Casual selfie photo</p>
                </div>

                <div className="hidden md:flex absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                  <div className="w-12 h-12 bg-throttle-cyan-500 rounded-full flex items-center justify-center shadow-lg shadow-throttle-cyan-500/50">
                    <svg className="w-6 h-6 text-throttle-navy-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </div>
                </div>

                <div className="flex md:hidden justify-center py-2">
                  <div className="w-12 h-12 bg-throttle-cyan-500 rounded-full flex items-center justify-center shadow-lg shadow-throttle-cyan-500/50">
                    <svg className="w-6 h-6 text-throttle-navy-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>
                </div>

                <div>
                  <div className="text-center mb-4">
                    <span className="inline-block px-4 py-2 bg-throttle-cyan-500 text-throttle-navy-900 rounded-full text-sm font-semibold mb-4 shadow-lg shadow-throttle-cyan-500/30">
                      AFTER
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="rounded-lg overflow-hidden border border-slate-600">
                      <img
                        src="/images/examples/image1 copy copy.png"
                        alt="Generated example - Professional real estate photo"
                        className="w-full h-auto"
                      />
                    </div>
                    <div className="rounded-lg overflow-hidden border border-slate-600">
                      <img
                        src="/images/examples/image2 copy copy.png"
                        alt="Generated example - Keys to new home"
                        className="w-full h-auto"
                      />
                    </div>
                    <div className="rounded-lg overflow-hidden border border-slate-600">
                      <img
                        src="/images/examples/image3 copy copy.png"
                        alt="Generated example - Sunset portrait"
                        className="w-full h-auto"
                      />
                    </div>
                    <div className="rounded-lg overflow-hidden border border-slate-600">
                      <img
                        src="/images/examples/image5 copy copy.png"
                        alt="Generated example - Working professional"
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                  <p className="text-gray-400 text-center mt-4 text-sm sm:text-base">Professional branded social content</p>
                </div>
              </div>
            </div>
          </div>

          {recentPosts.length > 0 && (
            <div className="mt-16 sm:mt-20 px-4">
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-header font-bold text-white mb-3">Latest Updates & Tips</h2>
                <p className="text-lg text-gray-300">Stay informed with our latest articles, best practices, and industry news</p>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {recentPosts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="bg-slate-800/50 backdrop-blur rounded-xl overflow-hidden border border-slate-700 hover:border-throttle-cyan-500 transition-all duration-200 hover:transform hover:scale-105 group"
                  >
                    {post.featured_image_url && (
                      <div className="overflow-hidden bg-slate-900/50">
                        <img
                          src={post.featured_image_url}
                          alt={post.title}
                          className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-200"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      {post.category_name && (
                        <span className="inline-block px-3 py-1 bg-throttle-cyan-500/20 text-throttle-cyan-400 rounded-full text-xs font-semibold mb-3">
                          {post.category_name}
                        </span>
                      )}
                      <h3 className="text-xl font-header font-bold text-white mb-2 group-hover:text-throttle-cyan-400 transition-colors">
                        {post.title}
                      </h3>
                      {post.excerpt && (
                        <p className="text-gray-400 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Calendar size={14} />
                          <span>{new Date(post.published_at!).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock size={14} />
                          <span>{post.reading_time_minutes} min read</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="text-center mt-8">
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-all duration-200 border border-white/30 hover:border-throttle-cyan-500 backdrop-blur"
                >
                  View All Articles
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          )}

          <div className="mt-16 sm:mt-24 px-4 py-16 sm:py-20 bg-slate-800/30 rounded-2xl backdrop-blur">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-accent text-white mb-4 text-center lowercase">ready to look like the market expert without a photo-shoot?</h2>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 text-center">Get your pack now.</p>
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={() => navigate('/pricing')}
                className="w-full sm:w-auto px-8 sm:px-12 py-4 sm:py-5 bg-throttle-cyan-500 text-throttle-navy-900 text-lg sm:text-xl font-header font-bold rounded-lg hover:bg-throttle-cyan-400 transition-all duration-200 min-h-[56px] shadow-2xl shadow-throttle-cyan-500/60 hover:shadow-throttle-cyan-500/80 hover:scale-105"
              >
                Start Your Pack Now
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
